/**
 * Client Privilege Service - Manages client-based page privileges for dynamic menu filtering
 * 
 * This service provides functionality to:
 * 1. Get client by user's program/batch code
 * 2. Filter routes based on client page privileges
 * 3. Cache client privilege data for better performance
 */

import { getAllClients, getProgramCodesFromClients } from '../components/Queries';

// Cache for storing client privilege data
let clientPrivilegeCache = null;
let programCodesPrivilegeCache = null;
let privilegeCacheTimestamp = null;
const PRIVILEGE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Clear the privilege cache
 */
export const clearClientPrivilegeCache = () => {
    clientPrivilegeCache = null;
    programCodesPrivilegeCache = null;
    privilegeCacheTimestamp = null;
};

/**
 * Check if privilege cache is valid
 */
const isPrivilegeCacheValid = () => {
    return privilegeCacheTimestamp && (Date.now() - privilegeCacheTimestamp) < PRIVILEGE_CACHE_DURATION;
};

/**
 * Load and cache client privilege data
 */
const loadPrivilegeData = async () => {
    if (isPrivilegeCacheValid() && clientPrivilegeCache && programCodesPrivilegeCache) {
        return { clients: clientPrivilegeCache, programCodes: programCodesPrivilegeCache };
    }

    try {
        console.log('[ClientPrivilegeService] Loading client and program codes data...');
        const [clientsResponse, programCodesResponse] = await Promise.all([
            getAllClients(),
            getProgramCodesFromClients()
        ]);

        clientPrivilegeCache = clientsResponse || [];
        programCodesPrivilegeCache = programCodesResponse || [];
        privilegeCacheTimestamp = Date.now();

        return { clients: clientPrivilegeCache, programCodes: programCodesPrivilegeCache };
    } catch (error) {
        console.error('[ClientPrivilegeService] Error loading client/program codes data:', error);
        return { clients: [], programCodes: [] };
    }
};

/**
 * Get client by user's program/batch code
 * 
 * @param {string} userBatchCode - The user's batch/program code
 * @returns {Promise<Object|null>} - Client object with page privileges or null
 */
export const getClientByUserBatch = async (userBatchCode) => {
    if (!userBatchCode || typeof userBatchCode !== 'string') {
        console.warn('[ClientPrivilegeService] No valid user batch code provided:', userBatchCode);
        return null;
    }

    try {
        const { clients, programCodes } = await loadPrivilegeData();

        // Handle comma-separated batch codes (user might have multiple batches)
        const sanitizedBatchCode = String(userBatchCode || '');
        const batchCodes = sanitizedBatchCode.split(',').map(code => String(code || '').trim()).filter(Boolean);
        
        for (const batchCode of batchCodes) {
            // Find the program entry with matching program code from clients programs
            const programEntry = programCodes.find(item => 
                item.name === batchCode
            );

            if (programEntry && programEntry.client_code) {
                // Find the client with matching client_code
                const client = clients.find(client => 
                    client.client_code === programEntry.client_code
                );

                if (client) {
                    console.log('[ClientPrivilegeService] Found client for batch code:', batchCode, client);
                    return client;
                }
            }
        }

        console.warn('[ClientPrivilegeService] No client found for batch codes:', batchCodes);
        return null;
    } catch (error) {
        console.error('[ClientPrivilegeService] Error getting client by user batch:', error);
        return null;
    }
};

/**
 * Get page privileges for a user based on their batch/program code
 * 
 * @param {string} userBatchCode - The user's batch/program code
 * @returns {Promise<string[]>} - Array of allowed page names/routes
 */
export const getUserPagePrivileges = async (userBatchCode) => {
    try {
        if (!userBatchCode || typeof userBatchCode !== 'string') {
            console.log('[ClientPrivilegeService] No valid user batch code for privileges, returning empty array');
            return [];
        }

        const client = await getClientByUserBatch(userBatchCode);
        
        if (!client || !client.page_privileges) {
            console.log('[ClientPrivilegeService] No page privileges found, returning empty array');
            return [];
        }

        // page_privileges is stored as an array in the client object
        const privileges = Array.isArray(client.page_privileges) ? client.page_privileges : [];
        console.log('[ClientPrivilegeService] User page privileges:', privileges);
        
        return privileges;
    } catch (error) {
        console.error('[ClientPrivilegeService] Error getting user page privileges:', error);
        return [];
    }
};

/**
 * Check if a route is allowed based on client page privileges
 * 
 * @param {Object} route - Route configuration object
 * @param {string[]} pagePrivileges - Array of allowed page names
 * @returns {boolean} - Whether the route is allowed
 */
export const isRouteAllowedByPrivileges = (route, pagePrivileges) => {
    // Always allow routes explicitly marked as alwaysVisible
    if (route?.alwaysVisible) {
        return true;
    }

    // In corporate mode, allow core corporate features even when privileges are defined
    const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';
    if (isCorporateMode) {
        const corporateCoreNames = ['Annual Review', 'Probation Review', 'User Management', 'Growth Conversations', 'Self-Reflect & Rise'];
        if (corporateCoreNames.includes(route?.name)) {
            return true;
        }
    }
    // If no page privileges are defined, allow all routes (backward compatibility)
    if (!pagePrivileges || pagePrivileges.length === 0) {
        return true;
    }

    // Check if route name or path is in the allowed privileges
    const routeName = route.name;
    const routePath = route.path;

    // Check by route name (primary method)
    if (routeName && pagePrivileges.includes(routeName)) {
        return true;
    }

    // Check by route path as fallback
    if (routePath && pagePrivileges.includes(routePath)) {
        return true;
    }

    // Check for common route name variations
    const routeVariations = [
        routeName,
        routeName?.toLowerCase(),
        routeName?.replace(/\s+/g, ''),
        routeName?.replace(/\s+/g, '_'),
        routePath?.replace('/', ''),
        routePath?.replace('/', '').toLowerCase()
    ].filter(Boolean);

    for (const variation of routeVariations) {
        if (pagePrivileges.includes(variation)) {
            return true;
        }
    }

    console.log('[ClientPrivilegeService] Route not allowed by privileges:', {
        routeName,
        routePath,
        pagePrivileges
    });

    return false;
};

/**
 * Filter routes based on client page privileges
 * 
 * @param {Array} routes - Array of route objects
 * @param {string} userBatchCode - User's batch/program code
 * @returns {Promise<Array>} - Filtered routes array
 */
export const filterRoutesByClientPrivileges = async (routes, userBatchCode) => {
    try {
        const pagePrivileges = await getUserPagePrivileges(userBatchCode);
        
        // If no privileges are defined, return all routes (backward compatibility)
        if (!pagePrivileges || pagePrivileges.length === 0) {
            console.log('[ClientPrivilegeService] No page privileges defined, allowing all routes');
            return routes;
        }

        const filteredRoutes = routes.filter(route => 
            isRouteAllowedByPrivileges(route, pagePrivileges)
        );

        console.log('[ClientPrivilegeService] Filtered routes by privileges:', {
            originalCount: routes.length,
            filteredCount: filteredRoutes.length,
            pagePrivileges,
            allowedRoutes: filteredRoutes.map(r => r.name)
        });

        return filteredRoutes;
    } catch (error) {
        console.error('[ClientPrivilegeService] Error filtering routes by client privileges:', error);
        // In case of error, return all routes to avoid breaking the application
        return routes;
    }
};

/**
 * Enhanced sidebar route filtering that combines role, environment, and client privilege filtering
 * 
 * @param {Array} routes - Array of route objects
 * @param {string} userRole - Current user role
 * @param {string} userBatchCode - User's batch/program code
 * @param {string} currentEnv - Current environment (optional)
 * @returns {Promise<Array>} - Filtered routes for sidebar
 */
export const getClientFilteredSidebarRoutes = async (routes, userRole, userBatchCode, currentEnv = null) => {
    try {
        console.log('[ClientPrivilegeService] Starting sidebar route filtering:', {
            routesCount: routes?.length,
            userRole,
            userBatchCode,
            userBatchCodeType: typeof userBatchCode
        });

        // First filter by client privileges
        let filteredRoutes = await filterRoutesByClientPrivileges(routes, userBatchCode);
        
        // Then apply existing role and environment filtering
        filteredRoutes = filteredRoutes.filter(route => {
            // Must have icon to appear in sidebar
            if (!route.icon) {
                return false;
            }

            // Always show routes explicitly marked as alwaysVisible
            if (route?.alwaysVisible) {
                return shouldDisplayInEnvironment(route, currentEnv);
            }

            // Check role-based access first (exact comma-split match)
            const hasRoleAccess = !route.role || route.role.split(',').map(r => r.trim()).includes(userRole);
            
            // Check environment-based access (using existing utility)
            const hasEnvironmentAccess = !route.environment || shouldDisplayInEnvironment(route, currentEnv);
            
            return hasRoleAccess && hasEnvironmentAccess;
        });

        console.log('[ClientPrivilegeService] Final filtered sidebar routes:', {
            originalCount: routes.length,
            finalCount: filteredRoutes.length,
            userRole,
            userBatchCode,
            routes: filteredRoutes.map(r => r?.name || 'unnamed')
        });

        return filteredRoutes;
    } catch (error) {
        console.error('[ClientPrivilegeService] Error in client filtered sidebar routes:', error);
        console.error('Error details:', error.stack);
        // Fallback: apply role filtering even on error
        return routes.filter(route => {
            if (!route?.icon) return false;
            if (!route.role) return true;
            return route.role.split(',').map(r => r.trim()).includes(userRole);
        });
    }
};

// Helper function for environment checking (imported from routeStatus)
const shouldDisplayInEnvironment = (route, currentEnv = null) => {
    const environment = currentEnv || process.env.REACT_APP_STAGE || 'DEV';
    
    if (!route.environment) {
        return true;
    }

    if (typeof route.environment === 'string') {
        const environments = route.environment
            .split(',')
            .map(env => String(env || '').trim().toUpperCase())
            .filter(Boolean);
        return environments.includes(environment.toUpperCase());
    }
    
    if (Array.isArray(route.environment)) {
        return route.environment
            .map(env => String(env || '').toUpperCase())
            .includes(environment.toUpperCase());
    }
    
    return (route.environment || '').toUpperCase() === environment.toUpperCase();
};

const clientPrivilegeService = {
    getClientByUserBatch,
    getUserPagePrivileges,
    isRouteAllowedByPrivileges,
    filterRoutesByClientPrivileges,
    getClientFilteredSidebarRoutes,
    clearClientPrivilegeCache
};

export default clientPrivilegeService;